/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { isRightToLeft } from 'js/lib/language';

import _t from 'i18n!nls/learning-assistant-chat-ai';

/**
 * Rebrand version of the CoachStylesProvider that is located in static/bundles/cds-labs/components/Coach/CoachStylesProvider.tsx
 * Simple wrapper that overrides the default CDS tokens to style with the "Coach theme"
 * All children of this component will be styled with the overrides
 *
 * Any customizations that are not doable in CDS will be done in this
 * provider e.g. custom button behavior for active state.
 *
 * @example
 *
 * <CoachStylesProviderV2>
 *   <div className="coach-app">
 *     <Typography variant="h3semibold">
 *       Welcome to Coach!
 *     </Typography>
 *     <Button variant="primary">
 *       Some coach action
 *     </Button>
 *
 *     <IconButton variant="ghost" intent="close" />
 *   </div>
 * </CoachStylesProviderV2>
 */

const CoachStylesProviderV2 = ({ children }: { children: React.ReactNode }) => {
  // eslint-disable-next-line import/no-named-as-default-member
  const isRTL = isRightToLeft(_t.getLocale());
  const defaultGradient = `linear-gradient(279deg, #0048B0 -5.78%, #1E72EB 83.48%)`;
  const interactedGradient = `linear-gradient(310deg, #0048b0 -10.46%, #1e72eb 78.54%, #66dab7 116.68%)`;

  return (
    <div
      data-theme="coach-web"
      css={css`
        /* CDS overrides */
        --cds-color-neutral-primary: #1e2229;
        --cds-color-neutral-primary-weak: #5b6780;
        --cds-color-neutral-primary-invert: #fff;
        --cds-color-neutral-stroke-primary: #dae1ed;
        --cds-color-neutral-stroke-primary-weak: #f4f7fa;
        --cds-color-neutral-background-primary: #fff;
        --cds-color-neutral-background-primary-weak: #f4f7fa;
        --cds-color-neutral-disabled: #dae1ed;
        --cds-color-neutral-disabled-strong: #dae1ed;
        --cds-color-neutral-disabled-weak: #fff;
        --cds-color-interactive-primary-selected: #f0f6ff;
        --cds-color-interactive-background-primary-hover-weak: #f0f6ff;
        --cds-color-interactive-background-primary-pressed-weak: #f0f6ff;
        --cds-color-interactive-background-primary-selected-weak: #f0f6ff;
        --cds-color-interactive-stroke-primary-focus: #002761;
        --cds-color-interactive-stroke-primary-focus-invert: #e3eeff;

        .coach-icon-button-v2 {
          --cds-border-radius-50: 50%;
          --cds-color-interactive-primary-selected: #1e72eb;

          box-shadow: none;

          &.selected {
            svg path {
              fill: var(--cds-color-interactive-primary-selected);
            }

            background: #f0f6ff;
          }

          &[aria-disabled='true'] {
            background-color: transparent;

            svg path {
              fill: var(--cds-color-neutral-disabled);
            }
          }
        }

        .coach-button-v2 {
          border-radius: var(--cds-border-radius-100);
          background: ${isRTL ? '#1e72eb' : defaultGradient};
          box-shadow: none;

          &:hover,
          &:active,
          &.selected {
            background: ${isRTL ? '#0048b0' : interactedGradient};
          }

          &.cds-button-focusVisible {
            background: ${isRTL ? '#0048b0' : interactedGradient};

            &::after {
              border-radius: 10px !important;
            }
          }

          &:active {
            border-radius: 12px;
            transition: all 0.2s cubic-bezier(0, 1, 0.9, 1);
            transform: scale3d(0.98, 0.98, 0.98);
          }

          &[aria-disabled='true'] {
            background: var(--cds-color-neutral-disabled);

            .cds-button-label {
              color: var(--cds-color-neutral-disabled-weak);
            }
          }

          &.coach-secondary-button {
            position: relative;
            z-index: 1;

            .cds-button-label {
              color: #0f1114;
            }

            &::before {
              content: '';
              position: absolute;
              left: 2px;
              right: 2px;
              top: 2px;
              bottom: 2px;
              border-radius: 6px;
              background-color: var(--cds-color-neutral-background-primary);
              z-index: -1;
            }

            &:active {
              &::before {
                border-radius: 10px;
              }
            }

            &.selected {
              &::before {
                background-color: var(--cds-color-interactive-primary-selected);
              }

              svg:not(.sparkle-icon) path {
                fill: #1e72eb;
              }
            }

            &:hover,
            &.cds-button-focusVisible,
            &.selected {
              box-shadow: none;

              .cds-button-label {
                color: #1e72eb;
              }

              svg:not(.sparkle-icon) path {
                fill: #1e72eb;
              }
            }

            &[aria-disabled='true'] {
              .cds-button-label {
                color: var(--cds-color-neutral-disabled);
              }

              svg path {
                fill: var(--cds-color-neutral-disabled);
              }
            }
          }
        }

        .coach-ghost-button {
          background: transparent;

          .cds-button-label {
            color: #0f1114;
          }

          &:hover,
          &:active,
          &.selected,
          &.cds-button-focusVisible {
            background: #f0f6ff;
            text-decoration: none;
          }

          &.selected {
            .cds-button-label {
              color: #1e72eb;
            }

            svg path {
              fill: #1e72eb;
            }
          }

          &:disabled {
            background: transparent;

            .cds-button-label {
              color: var(--cds-color-neutral-disabled);
            }

            svg path {
              fill: var(--cds-color-neutral-disabled);
            }
          }
        }
      `}
    >
      {children}
    </div>
  );
};

export default CoachStylesProviderV2;
